import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import s from '../Themes.module.css';
import { useAuth } from '../context/AuthContext';
import { useAppTranslation } from "./TranslationContext";

export const Themes = () => {
  const { id } = useParams(); // kursId
  const { user, handleLang } = useAuth();
  const { t } = useAppTranslation();

  const [themes, setThemes] = useState([]);
  const [kursNameUz, setKursNameUz] = useState('');
  const [kursNameRu, setKursNameRu] = useState('');
  const [kursNameEn, setKursNameEn] = useState('');
  const [kursImg, setKursImg] = useState('');

  const [loadingThemes, setLoadingThemes] = useState(true);
  const [loadingUserKurs, setLoadingUserKurs] = useState(true);

  const [errorThemes, setErrorThemes] = useState(null);
  const [openIndex, setOpenIndex] = useState(null);
  const [userKurs, setUserKurs] = useState(null);

  // === Получение данных курса ===
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await fetch(`https://api.eduskill.uz/api/kurslar/${id}/`);
        if (!res.ok) throw new Error(`Ошибка получения курса: ${res.status}`);
        const data = await res.json();
        setKursNameUz(data?.nomi_uz ?? '');
        setKursNameRu(data?.nomi_ru ?? '');
        setKursNameEn(data?.nomi_en ?? '');
        setKursImg(data?.img1_uz ?? '');
      } catch (err) {
        console.error("Ошибка загрузки курса:", err);
      }
    })();
  }, [id]);

  // === КЕШИРОВАННАЯ загрузка тем ===
  useEffect(() => {
    if (!id) return;

    const cacheKey = `kurs_themes_cache_${id}`;
    const cached = localStorage.getItem(cacheKey);

    const tryCache = () => {
      if (!cached) return false;
      try {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        const weekMs = 7 * 24 * 60 * 60 * 1000;
        if (now - parsed.timestamp < weekMs && Array.isArray(parsed.data)) {
          setThemes(parsed.data);
          setLoadingThemes(false);
          return true;
        }
      } catch {}
      return false;
    };

    if (tryCache()) return;

    (async () => {
      try {
        setLoadingThemes(true);
        const res = await fetch(`https://api.eduskill.uz/api/kurs-mavzulari/?kurs=${id}`);
        if (!res.ok) throw new Error("Ошибка сети: " + res.status);

        const json = await res.json();
        const sorted = json
          .map(i => ({
            tartib: Number(i.tartib),
            id: i.id,
            kurs_id: id,
            title_uz: i.nomi_uz,
            title_ru: i.nomi_ru,
            title_en: i.nomi_en,
            image: i.mrasmi_uz,
            desc_uz: i.short_text_uz,
            desc_ru: i.short_text_ru,
            desc_en: i.short_text_en,
            test_soni: Number(i.test_soni ?? 0),
            test_min: Number(i.test_min_soni ?? 0),
            test_description_uz: i.test_description_uz,
            test_description_ru: i.test_description_ru,
            test_description_en: i.test_description_en
          }))
          .sort((a, b) => a.tartib - b.tartib);

        setThemes(sorted);
        localStorage.setItem(cacheKey, JSON.stringify({
          timestamp: Date.now(),
          data: sorted
        }));
      } catch (err) {
        setErrorThemes(err.message);
      } finally {
        setLoadingThemes(false);
      }
    })();
  }, [id]);

  // === Загрузка user-kurs ===
  useEffect(() => {
    (async () => {
      try {
        setLoadingUserKurs(true);
        const token = localStorage.getItem('token');
        const userId = user?.id;
        if (!token || !userId || !id) {
          setUserKurs(null);
          return;
        }

        const res = await fetch(`https://api.eduskill.uz/api/user-kurs/?user=${userId}&kurs=${id}`, {
          headers: { 'Authorization': `Token ${token}` }
        });
        if (!res.ok) throw new Error('Ошибка при получении user-kurs');
        const data = await res.json();
        setUserKurs(data?.[0] ?? null);
      } catch (error) {
        console.error("Ошибка сети:", error);
        setUserKurs(null);
      } finally {
        setLoadingUserKurs(false);
      }
    })();
  }, [user, id]);

  const toggle = (index) => setOpenIndex(openIndex === index ? null : index);

  // === Вычисления ===
  // БЫЛО: берём количество по userKurs.mavzu
  // СТАЛО: берём tartib из userKurs (число последней ДОСТУПНОЙ/пройденной темы)
  const total = themes.length;
  const completedRaw = Number(userKurs?.tartib ?? 0);     // ← ИЗМЕНЕНО
  const completed = Number.isFinite(completedRaw) ? completedRaw : 0;

  // Если userKurs.tartib — количество уже пройденных тем,
  // то доступна следующая: tartib <= completed + 1
  // Если это "номер текущей темы" — переключите useCountModel на false
  const useCountModel = false;

  const isTopicAccessible = (tartib) => {
    return useCountModel ? (tartib <= completed + 1) : (tartib <= completed);
  };

  // Финальный тест доступен, когда все темы пройдены
  const canStartFinal = completed >= total;

  if (errorThemes) return <p className='container'>Ошибка загрузки тем: {errorThemes}</p>;
  if (loadingThemes || loadingUserKurs) return <p className='container'>Ma'lumotlar yuklanmoqda...</p>;

  return (
    <section className={s.course__intro}>
      <div className={s.c__intro__sidebar}>
        <div className={s.c__intro__sidebar_info}>
          <div className={s.c__intro__sidebar_title}>
            {handleLang(kursNameUz, kursNameRu, kursNameEn)}
          </div>
          <div className={s.c__intro__sidebar_num}>
            {Math.min(completed, total)}/{total} {t('themes')}
          </div>
          {kursImg && (
            <img
              src={kursImg}
              alt="Картинка курса"
              className={s.c__intro__sidebar_img}
              loading="lazy"
            />
          )}
        </div>
      </div>

      <div className={s.c__intro__content}>
        {themes.map((item, index) => {
          const accessible = isTopicAccessible(item.tartib);

          const content = (
            <>
              {item.image && (
                <img
                  src={item.image}
                  className={s.video_thumb}
                  alt="Видео превью"
                  loading="lazy"
                />
              )}
              <p>{handleLang(item.desc_uz, item.desc_ru, item.desc_en)}</p>
            </>
          );

          return (
            <div key={item.id ?? index} className={s.accordion_item}>
              <div
                className={s.accordion_header}
                onClick={() => toggle(index)}
                aria-expanded={openIndex === index}
                aria-controls={`panel-${item.id}`}
                id={`header-${item.id}`}
              >
                {handleLang(item.title_uz, item.title_ru, item.title_en)}
                <span className={`${s.arrow} ${openIndex === index ? s.open : ''}`}>▶</span>
              </div>

              <div
                id={`panel-${item.id}`}
                className={`${s.accordion_content} ${openIndex === index ? s.open : ''}`}
                role="region"
                aria-labelledby={`header-${item.id}`}
              >
                {accessible ? (
                  <Link className={s.sub_item} to={`/Lesson/${id}/${item.id}`}>
                    {content}
                  </Link>
                ) : (
                  <div
                    className={s.sub_item}
                    style={{ pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' }}
                  >
                    {content}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Финальный тест ТЕПЕРЬ ведёт на /Test/:kursId  */}
        {canStartFinal ? (
          <Link className={`${s.sub_item} ${s.sub_item_fin}`} to={`/Test/${id}`}>
            <div className={`${s.accordion_item} ${s.yakuniy}`}>
              <div className={s.accordion_header}>
                {t('themesFin')} <span className={s.arrow}>▶</span>
              </div>
            </div>
          </Link>
        ) : (
          <div
            className={`${s.sub_item} ${s.sub_item_fin}`}
            style={{ pointerEvents: 'none', opacity: 0.5, cursor: 'not-allowed' }}
          >
            <div className={`${s.accordion_item} ${s.yakuniy}`}>
              <div className={s.accordion_header}>
                {t('themesFin')} <span className={s.arrow}>▶</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
